/**
 * PBI 1962 (AB#1968) — pure mapping/validation helpers for the option-groups
 * builder. No React, no I/O → unit-testable in isolation.
 */
import type {
    OptionGroupModel,
    OptionRowModel,
    OptionGroupWirePayload,
    ServiceOptionGroupDto,
} from '../../types/serviceOptionGroups';
import { serviceOptionGroupsSchema } from '../../schemas/serviceOptionGroups.schema';

/** Cheap unique key for client-side React keys / reorder ops. */
let keySeq = 0;
export const nextKey = (prefix = 'k'): string =>
    `${prefix}-${Date.now().toString(36)}-${(keySeq++).toString(36)}`;

/** "HH:mm:ss" → whole minutes (rounded). Tolerates "mm:ss" and bad input → 0. */
export const durationStringToMinutes = (raw: string | null | undefined): number => {
    if (!raw) return 0;
    const parts = raw.split(':').map((p) => Number(p));
    if (parts.some((n) => Number.isNaN(n))) return 0;
    let h = 0;
    let m = 0;
    let s = 0;
    if (parts.length === 3) [h, m, s] = parts;
    else if (parts.length === 2) [m, s] = parts;
    else if (parts.length === 1) [m] = parts;
    return Math.max(0, Math.round(h * 60 + m + s / 60));
};

/** whole minutes → "HH:mm:ss" (backend TimeSpan string). */
export const minutesToDurationString = (min: number): string => {
    const safe = Math.max(0, Math.round(min));
    const h = Math.floor(safe / 60);
    const m = safe % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:00`;
};

/** A fresh, empty option row. */
export const makeEmptyOption = (): OptionRowModel => ({
    key: nextKey('opt'),
    name: '',
    nameAr: '',
    price: 0,
    durationMin: 0,
});

/** A fresh Add-on group (the default new group — the single Variant already exists). */
export const makeEmptyAddOnGroup = (): OptionGroupModel => ({
    key: nextKey('grp'),
    name: '',
    nameAr: '',
    kind: 'AddOn',
    selectionType: 'Multiple',
    minSelect: 0,
    maxSelect: 3,
    isImplicit: false,
    options: [makeEmptyOption()],
});

/** A fresh Variant group (used to seed a net-new service with no groups). */
export const makeDefaultVariantGroup = (
    seed?: Array<{ name: string; nameAr?: string | null; price: number; durationMin: number; id?: number }>,
): OptionGroupModel => ({
    key: nextKey('grp'),
    name: 'Options',
    nameAr: 'الخيارات',
    kind: 'Variant',
    selectionType: 'Single',
    minSelect: 1,
    maxSelect: 1,
    isImplicit: false,
    options:
        seed && seed.length > 0
            ? seed.map((o) => ({
                  key: nextKey('opt'),
                  id: o.id,
                  name: o.name,
                  nameAr: o.nameAr ?? '',
                  price: o.price,
                  durationMin: o.durationMin,
              }))
            : [makeEmptyOption()],
});

/**
 * DTO (server read shape) → editor model. Preserves server ids + sort order and
 * the legacy `isImplicit` auto-wrap marker so the builder can badge it.
 */
export const dtoToModel = (groups: ServiceOptionGroupDto[]): OptionGroupModel[] =>
    [...groups]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((g) => ({
            key: nextKey('grp'),
            id: g.id,
            name: g.name,
            nameAr: g.nameAr ?? '',
            kind: g.kind,
            selectionType: g.selectionType,
            minSelect: g.minSelect,
            maxSelect: g.maxSelect,
            isImplicit: g.isImplicit ?? false,
            options: [...g.options]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((o) => ({
                    key: nextKey('opt'),
                    id: o.id,
                    name: o.name,
                    nameAr: o.nameAr ?? '',
                    price: o.price,
                    durationMin: durationStringToMinutes(o.duration),
                })),
        }));

/**
 * Build the legacy flat pricing options fallback from the (single) Variant
 * group. The backend keeps `PricingOptionsJson` authoritative for old readers
 * and back-populates it from the Variant group; sending it in parallel keeps
 * the dual-write contract satisfied while the flag is dark.
 */
export const modelToFlatPricingOptions = (
    groups: OptionGroupModel[],
    currency: string,
): Array<{ name: string; price: number; currency: string; duration: string }> => {
    const variant = groups.find((g) => g.kind === 'Variant');
    const source = variant ?? groups[0];
    if (!source) return [];
    return source.options
        .filter((o) => o.name.trim().length > 0)
        .map((o) => ({
            name: o.name.trim(),
            price: o.price,
            currency,
            duration: minutesToDurationString(o.durationMin),
        }));
};

/**
 * editor model → `OptionGroupsJson` wire payload (Faris §6.1). Assigns 0-based
 * sortOrder from array position; trims names; applies the service-wide currency
 * (single currency per service). Empty AR names serialize as null.
 */
export const modelToWirePayload = (
    groups: OptionGroupModel[],
    currency: string,
): OptionGroupWirePayload[] =>
    groups.map((g, gi) => ({
        name: g.name.trim(),
        nameAr: g.nameAr.trim() ? g.nameAr.trim() : null,
        selectionType: g.selectionType,
        minSelect: g.minSelect,
        maxSelect: g.maxSelect,
        kind: g.kind,
        sortOrder: gi,
        options: g.options.map((o, oi) => ({
            name: o.name.trim(),
            nameAr: o.nameAr.trim() ? o.nameAr.trim() : null,
            price: o.price,
            currency,
            duration: minutesToDurationString(o.durationMin),
            sortOrder: oi,
        })),
    }));

export interface OptionGroupsValidationResult {
    ok: boolean;
    /** i18n keys for each distinct error message (deduped). */
    errorKeys: string[];
}

/**
 * Validate the editor model against the mirrored backend rules. Returns i18n
 * keys (deduped) so the builder can render them via t(). The backend remains
 * authoritative — this is advisory, fast feedback.
 */
export const validateOptionGroups = (
    groups: OptionGroupModel[],
): OptionGroupsValidationResult => {
    const result = serviceOptionGroupsSchema.safeParse(groups);
    if (result.success) return { ok: true, errorKeys: [] };
    const keys = Array.from(
        new Set(result.error.issues.map((i) => i.message)),
    );
    return { ok: false, errorKeys: keys };
};
